import { useState, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { MapPin, Phone, Car, Navigation, Clock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { useBookingLinks } from "../context/BookingLinksContext";

const LocationsPage = () => {
  const [locations, setLocations] = useState([]);
  const [allHours, setAllHours] = useState([]);
  const [liveStatus, setLiveStatus] = useState([]);
  const bookingLinks = useBookingLinks();

  useEffect(() => {
    axios.get(`${API}/locations`).then(res => setLocations(res.data.filter(l => l.id !== "head_office"))).catch(() => {});
    axios.get(`${API}/opening-hours`).then(res => setAllHours(res.data)).catch(() => {});
    axios.get(`${API}/opening-hours/status`).then(res => setLiveStatus(res.data)).catch(() => {});
  }, []);

  const getHoursForLocation = (locId) => {
    const found = allHours.find(h => h.location_id === locId);
    return found ? found.hours : [];
  };

  const getStatus = (locId) => liveStatus.find(s => s.location_id === locId);

  const colors = ["#ff3131", "#7dbff8"];

  return (
    <div className="min-h-screen" data-testid="locations-page">
      <Header />
      
      <main>
        <section className="py-12 md:py-16 bg-gradient-to-b from-[#fff5f5] to-[#FFFBF5]">
          <div className="container-gymtots text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>
              Our <span className="text-[#ff3131]">Locations</span>
            </h1>
            <p className="text-[#636E72] max-w-xl mx-auto">
              Two convenient locations in Southampton. Find your nearest Gymtots today!
            </p>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-white">
          <div className="container-gymtots">
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-8">
              {locations.map((loc, i) => {
                const color = colors[i % colors.length];
                const mapUrl = loc.map_url || `https://maps.google.com/?q=${encodeURIComponent(loc.postcode)}`;
                const hours = getHoursForLocation(loc.id);
                const status = getStatus(loc.id);
                return (
                  <div 
                    key={loc.id}
                    className="bg-[#FFFBF5] rounded-2xl overflow-hidden border-t-4"
                    style={{ borderTopColor: color }}
                    data-testid={`location-card-${loc.id}`}
                  >
                    <div className="h-48 bg-gray-200 relative">
                      <iframe
                        src={`https://maps.google.com/maps?q=${encodeURIComponent(loc.postcode)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                        className="w-full h-full"
                        frameBorder="0"
                        allowFullScreen
                        loading="lazy"
                        title={`Map of ${loc.display_name}`}
                      />
                    </div>
                    
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-2xl font-bold text-[#2D3436]" style={{ fontFamily: 'Fredoka, cursive' }}>
                          {loc.display_name}
                        </h2>
                        {status && (
                          <span className={`text-xs font-bold px-3 py-1 rounded-full ${status.is_open_now ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                            {status.is_open_now ? 'Open Now' : 'Closed'}
                          </span>
                        )}
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color }} />
                          <div>
                            <p className="font-semibold text-[#2D3436]">{loc.address}</p>
                            <p className="text-[#636E72]">{loc.postcode}</p>
                          </div>
                        </div>
                        
                        {loc.phone && (
                          <div className="flex items-center gap-3">
                            <Phone className="w-5 h-5 flex-shrink-0" style={{ color }} />
                            <a href={`tel:${loc.phone.replace(/\s/g, '')}`} className="font-semibold hover:underline" style={{ color }}>
                              {loc.phone}
                            </a>
                          </div>
                        )}
                        
                        {loc.parking && (
                          <div className="flex items-start gap-3">
                            <Car className="w-5 h-5 flex-shrink-0 mt-0.5 text-[#636E72]" />
                            <p className="text-[#636E72]">{loc.parking}</p>
                          </div>
                        )}
                        
                        {loc.nearby && (
                          <div className="flex items-start gap-3">
                            <Navigation className="w-5 h-5 flex-shrink-0 mt-0.5 text-[#636E72]" />
                            <p className="text-[#636E72]">{loc.nearby}</p>
                          </div>
                        )}
                      </div>

                      {/* Opening Hours */}
                      {hours.length > 0 && (
                        <div className="mt-5 pt-5 border-t border-gray-100">
                          <div className="flex items-center gap-2 mb-3">
                            <Clock className="w-4 h-4" style={{ color }} />
                            <h3 className="font-bold text-sm text-[#2D3436]" style={{ fontFamily: 'Fredoka, cursive' }}>Opening Hours</h3>
                          </div>
                          <div className="space-y-1.5">
                            {hours.map((h, j) => (
                              <div key={j} className={`flex justify-between text-sm ${h.is_open ? 'text-[#2D3436]' : 'text-[#636E72]'}`}>
                                <span className="font-medium">{h.day}</span>
                                <span>{h.is_open ? `${h.open_time} - ${h.close_time}` : 'Closed'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <a
                        href={mapUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-6 w-full block text-center py-3 rounded-full font-bold text-white transition-colors"
                        style={{ fontFamily: 'Fredoka, cursive', backgroundColor: color }}
                      >
                        Get Directions
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Head Office */}
            <div className="max-w-5xl mx-auto">
              <div className="bg-[#1656a4] rounded-2xl p-6 text-white">
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Fredoka, cursive' }}>
                      Head Office - Dynamo Gymnastics
                    </h3>
                    <p className="text-white/80 text-sm mb-2">
                      Dynamo Gymnastics, Hamble Sports Complex<br/>
                      Satchell Lane, Hamble-le-Rice, SO31 4NE
                    </p>
                    <p className="text-white/60 text-xs">For all administrative enquiries</p>
                  </div>
                  <div className="flex gap-3">
                    <a href="tel:02380455007" className="bg-white/20 hover:bg-white/30 px-6 py-3 rounded-full font-bold text-sm transition-colors">
                      02380 455007
                    </a>
                    <a href="mailto:admin@gym-tots.co.uk" className="bg-[#7dbff8] hover:bg-[#6ca6d9] text-white px-6 py-3 rounded-full font-bold text-sm transition-colors">
                      Email Us
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-[#FFFBF5]">
          <div className="container-gymtots text-center max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>
              Find Your <span className="text-[#7dbff8]">Nearest Class</span>
            </h2>
            <p className="text-[#636E72] mb-6">
              Enter your postcode in our class finder to see which location is closest to you.
            </p>
            <a
              href={bookingLinks.general || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-[#ff3131] hover:bg-[#e62e2e] text-white px-8 py-4 rounded-full font-bold text-lg transition-colors"
              style={{ fontFamily: 'Fredoka, cursive' }}
            >
              Book a Trial Session!
            </a>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default LocationsPage;
